import { DeferredImage, useBreakpoint } from '@vivino/js-react-common-ui';
import { Anchor, BottleSize, useI18N } from '@vivino/js-web-common';
import cx from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { foodUrl } from 'vivino-js/vivinoUrls';

import BottleShot from '@components/BottleShot/BottleShot';

import { Col, Row } from '../Grid';
import styles from './foodPairing.scss';

const TRANSLATIONS_PATH = 'components.shared.food_pairing';
const TRANSLATIONS = {
  foodThatGoesWell: `${TRANSLATIONS_PATH}.food_that_goes_well_with_this_wine`,
  expertOpinion: `${TRANSLATIONS_PATH}.expert_opinion`,
  areYouCooking: `${TRANSLATIONS_PATH}.are_you_cooking`,
  winesByFoodPairing: `${TRANSLATIONS_PATH}.wines_by_food_pairings`,
};

const foodLinkOverrides = {
  21: 'https://www.vivino.com/wine-news/the-best-hamburger-wine-pairings',
  28: 'https://www.vivino.com/wine-news/the-insider-guide-to-wine-and-fish-pairing',
  38: 'https://www.vivino.com/wine-news/the-best-blue-cheese-wine-pairing-elevate-your-tasting-experience',
  10: 'https://www.vivino.com/wine-news/the-perfect-pork-and-wine-pairing-guide',
  11: 'https://www.vivino.com/wine-news/wild-meets-wine-the-ultimate-guide-to-wine-pairing-for-venison',
  13: 'https://www.vivino.com/wine-news/best-wine-with-shellfish-a-match-made-in-coastal-heaven',
  9: 'https://www.vivino.com/wine-news/the-ins-and-outs-of-veal-and-wine-pairing',
  15: 'https://www.vivino.com/wine-news/wine-pairing-for-spicy-food-a-how-to-guide',
  34: 'https://www.vivino.com/wine-news/the-best-wine-with-mushrooms-perfect-pairing-tips-for-flavorful-dishes',
  37: 'https://www.vivino.com/wine-news/the-ultimate-guide-to-fruit-and-wine-pairing',
  8: 'https://www.vivino.com/wine-news/master-the-art-of-lamb-wine-pairing',
  4: 'https://www.vivino.com/wine-news/discover-the-best-wine-with-beef-a-cut-by-cut-guide',
  12: 'https://www.vivino.com/wine-news/wine-pairing-with-salmon-the-ultimate-guide-to-finding-the-perfect-match',
  39: 'https://www.vivino.com/wine-news/goat-cheese-wine-pairing-a-match-made-in-cheese-snacking-heaven',
  5: 'https://www.vivino.com/wine-news/the-best-wines-to-pair-with-pasta',
  27: 'https://www.vivino.com/wine-news/delicious-appetizers-for-wine-tasting-that-will-impress-your-guests',
  16: 'https://www.vivino.com/wine-news/the-only-guide-to-dessert-wine-pairing-you-need',
  19: 'https://www.vivino.com/wine-news/fresh-fun-and-flavorful-the-art-of-pairing-wine-with-salad',
  41: 'https://www.vivino.com/wine-news/the-ultimate-guide-to-perfect-pairings-of-charcuterie-and-wine',
  40: 'https://www.vivino.com/wine-news/aperitif-hour-is-back-heres-what-to-pour',
  20: 'https://www.vivino.com/wine-news/ultimate-guide-what-wine-goes-with-chicken',
};

const FoodPairing = ({ vintage }) => {
  const wineStyle = vintage.wine?.style;
  const foodPairings = vintage.wine?.foods || [];
  const { isMobile, isTablet } = useBreakpoint();
  const { t } = useI18N();

  if (!wineStyle || foodPairings.length < 1) {
    return null;
  }
  return (
    <Row>
      <Col desktop={3} tablet={10} tabletOffsetRight={2}>
        <h2 className={styles.header}>{t(TRANSLATIONS.foodThatGoesWell)}</h2>
        <div className={styles.expertOpinion}>
          {t(TRANSLATIONS.expertOpinion, { wine_style: wineStyle?.name })}
        </div>
        <div className={styles.exploreLink}>
          {t(TRANSLATIONS.areYouCooking, {
            wines_by_food_pairings: (
              <Anchor href="/explore" className={styles.link} key="explore">
                {t(TRANSLATIONS.winesByFoodPairing)}
              </Anchor>
            ),
          })}
        </div>
      </Col>
      {!isMobile && (
        <Col desktop={2} tablet={2}>
          <BottleShot
            className={styles.bottleShot}
            image={vintage?.image}
            size={isTablet ? BottleSize.Tiny : BottleSize.Small}
            alt={vintage?.name}
            scale
          />
        </Col>
      )}
      <Col desktop={7} tablet={10}>
        <div className={styles.foodContainer}>
          {foodPairings.map((food) => {
            const overriddenUrl = foodLinkOverrides[food.id];
            const href = overriddenUrl || (food?.seo_name && foodUrl({ food }));

            return (
              <Anchor
                {...(href && { href })}
                key={food.id}
                className={cx(styles.imageContainer, {
                  [styles.unlinkable]: !href,
                })}
              >
                <DeferredImage src={food.background_image.variations.small}>
                  <div role="img" aria-label={food.name} className={styles.foodImage} />
                </DeferredImage>
                <div>{food.name}</div>
              </Anchor>
            );
          })}
        </div>
      </Col>
    </Row>
  );
};

FoodPairing.propTypes = {
  vintage: PropTypes.object.isRequired,
};

export default FoodPairing;
